import React, { useMemo, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Commodity.module.scss';
import Button from '../../../../components/button/button';
import FormNewCheckbox from '../../../../components/formCheckbox/FormNewCheckbox';
import FormDate from '../../../../components/formDate/FormDate';
import FormDropdown from '../../../../components/formDropdown/FormDropdown';
import FormInput from '../../../../components/formInput/FormInput';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import InputContainer from '../../../../components/template/InputContainer/InputContainer';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore, { Form } from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface CommodityProps {}

const Commodity: React.FC<CommodityProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext<Form>();
  const {
    setValue,
    getValues,
    formState: { isValid },
    watch,
    reset,
  } = formMethods;
  const { commodity } = watch();
  //#endregion

  const CommodityData = {
    title: 'Commodity risk',
    description:
      'Commodity risk is the potential for changes in commodity prices to result in losses.',
    compulsoryQuestions: [
      {
        id: 'commodity.compulsory',
        question: 'Is your Company exposed to commodity risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'commodity.probability',
        question: 'How frequent is the Company facing commodity risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
  };

  const commodityOptions = [
    {
      label: 'Metals',
      value: 1,
      suboptions: [
        {
          label: 'Gold',
          value: 1,
        },
        {
          label: 'Silver',
          value: 2,
        },
        {
          label: 'Copper',
          value: 3,
        },
        {
          label: 'Platinum',
          value: 4,
        },
      ],
    },
    {
      label: 'Energy',
      value: 2,
      suboptions: [
        {
          label: 'Crude oil',
          value: 1,
        },
        {
          label: 'Natural gas',
          value: 2,
        },
      ],
    },
    {
      label: 'Softs',
      value: 3,
      suboptions: [
        {
          label: 'Cotton',
          value: 1,
        },
        {
          label: 'Wheat',
          value: 2,
        },
        {
          label: 'Sugar',
          value: 3,
        },
        {
          label: 'Coffee',
          value: 4,
        },
        {
          label: 'Cocoa',
          value: 5,
        },
      ],
    },
  ];

  const instrumentsOptions = useMemo(() => {
    const typesOfCommodity = commodity?.typesOfCommodity;

    return commodityOptions.find((opt) => opt.value == typesOfCommodity)
      ?.suboptions;
  }, [commodity?.typesOfCommodity]);

  const isCalculateDisabled = useMemo(() => {
    return (
      !watch('commodity.typesOfCommodity') ||
      !watch('commodity.instruments') ||
      !watch('commodity.entryPrice') ||
      !watch('commodity.exitPrice') ||
      !watch('commodity.openDate') ||
      !watch('commodity.closeDate') ||
      !watch('commodity.direction') ||
      !watch('commodity.quantity')
    );
  }, [watch()]);

  const expectationOptions = [
    {
      label: 'Yes, even above my expectation',
      value: 1,
    },
    {
      label: 'Yes, within my expectation',
      value: 2,
    },
    {
      label: 'Neither above nor below my expectation',
      value: 3,
    },
    {
      label: 'No, below my expectation',
      value: 4,
    },
    {
      label: 'Not sure / I don’t know',
      value: 5,
    },
  ];

  //#region Handler
  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = CommodityData.compulsoryQuestions.map(
      (comp) => {
        return {
          answer: getValues('commodity.compulsory'),
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
        };
      }
    );

    compulsoryQuestions.push(
      ...([
        {
          answer: getValues('commodity.typesOfCommodity'),
          answerNotes: [],
          question: 'Types of commodity',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.instruments'),
          answerNotes: [],
          question: 'Instruments',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.entryPrice'),
          answerNotes: [],
          question: 'Entry price',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.exitPrice'),
          answerNotes: [],
          question: 'Exit price',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.openDate'),
          answerNotes: [],
          question: 'Open date',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.closeDate'),
          answerNotes: [],
          question: 'Close date',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.direction'),
          answerNotes: [],
          question: 'Buy or Sell',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.quantity'),
          answerNotes: [],
          question: 'Quantity',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.spread'),
          answerNotes: [],
          question: 'Spread',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.conversionFee'),
          answerNotes: [],
          question: 'Conversion fee',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.overnightSwaps'),
          answerNotes: [],
          question: 'Overnight swaps',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.commission'),
          answerNotes: [],
          question: 'Commission',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.pl1'),
          answerNotes: [],
          question: 'Total P/L (display in symbol)',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.pl2'),
          answerNotes: [],
          question: 'Total P/L (display in account)',
          questionNotes: [],
        },
        {
          answer: getValues('commodity.currConversionPrice'),
          answerNotes: [],
          question: 'Current conversion price',
          questionNotes: [],
        },
      ] as any)
    );

    const finalCommodityData = new QuestionForm(
      CommodityData.title,
      CommodityData.description,
      compulsoryQuestions,
      [
        {
          answer:
            expectationOptions.find(
              (item) => item.value == getValues('commodity.expectation')
            )?.label || '',
          answerNotes: [],
          marks: +getValues('commodity.expectation'),
          question: 'Is the result within your expectation?',
          questionNotes: [],
        },
        // {
        //   answer: getValues('commodity.typesOfCommodity'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Choose the types of commodity',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.instruments'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Choose the instruments',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.entryPrice'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Entry price',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.exitPrice'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Exit price',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.openDate'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Open date',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.closeDate'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Close date',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.direction') == 1 ? 'Buy' : 'Sell',
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Direction',
        //   questionNotes: [],
        // },
        // {
        //   answer: getValues('commodity.quantity'),
        //   answerNotes: [],
        //   marks: 0,
        //   question: 'Quantity',
        //   questionNotes: [],
        // },
      ],
      CommodityData.probabilityQuestions.map((comp) => {
        return {
          answer:
            comp.inputs.find(
              (inp) => inp.value == Number(getValues('commodity.probability'))
            )?.label || '',
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
          marks: Number(getValues('commodity.probability')),
        };
      })
    );

    setQuestion({ commodity: finalCommodityData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      commodity?.compulsory == '' ? undefined : commodity?.compulsory == 'Yes'
    );
  };

  const generateRandomData = () => {
    const randomNumber = (min = 1, max = 100000) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    const randomDate = (start: Date, end: Date) => {
      const date = new Date(
        start.getTime() + Math.random() * (end.getTime() - start.getTime())
      );
      return date.toISOString().split('T')[0]; // yyyy-mm-dd
    };

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: randomNumber(1, 5),

      typesOfCommodity: randomNumber(1, 3).toString(),

      instruments: randomNumber(1, 2).toString(),

      entryPrice: randomNumber(1000, 5000).toString(),
      exitPrice: randomNumber(1000, 5000).toString(),

      openDate: randomDate(new Date(2025, 0, 1), new Date(2025, 11, 31)),
      closeDate: randomDate(new Date(2025, 0, 1), new Date(2025, 11, 31)),

      direction: randomNumber(1, 2),

      quantity: randomNumber(1, 1000).toString(),
      spread: randomNumber(1, 1000).toString(),
      conversionFee: randomNumber(1, 1000).toString(),
      overnightSwaps: randomNumber(1, 1000).toString(),
      commission: randomNumber(1, 1000).toString(),
      currConversionPrice: randomNumber(1, 1000).toString(),

      expectation: randomNumber(1, 5).toString(),
    };
  };
  //#endregion

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="commodity"
          title={CommodityData.title}
          description={CommodityData.description}
          compulsoryQuestions={CommodityData.compulsoryQuestions}
          probabilityQuestions={CommodityData.probabilityQuestions}
        />

        {commodity?.compulsory && commodity?.compulsory == 'Yes' && (
          <SectionContainer>
            <hr />
            <Title>Commodities Profit Calculator</Title>
            <InputContainer>
              <FormDropdown
                id="commodity.typesOfCommodity"
                label="Types of commodity"
                placeholder="Choose the types of commodity"
                options={commodityOptions || []}
                onChange={() => setValue('commodity.instruments', '')}
                required
              />

              <FormDropdown
                id="commodity.instruments"
                label="Instruments"
                placeholder="Choose the instruments"
                options={instrumentsOptions || []}
                required
              />

              <FormInput
                id="commodity.entryPrice"
                label="Entry price"
                placeholder="Enter entry price"
                type="number"
                required
              />

              <FormInput
                id="commodity.exitPrice"
                label="Exit price"
                placeholder="Enter exit price"
                type="number"
                required
              />

              <FormDate
                id="commodity.openDate"
                label="Open date"
                placeholder="Enter open date"
                type="date"
                required
              />

              <FormDate
                id="commodity.closeDate"
                label="Close date"
                placeholder="Enter close date"
                type="date"
                required
              />

              <div>
                <FormDropdown
                  id="commodity.direction"
                  label="Buy or Sell"
                  placeholder="Choose buy or sell"
                  options={[
                    {
                      label: 'Buy',
                      value: 1,
                    },
                    {
                      label: 'Sell',
                      value: 2,
                    },
                  ]}
                  required
                />
              </div>

              <div></div>

              {/***
               * based on commodity type
               * energy & soft must start with 10
               */}
              <FormInput
                id="commodity.quantity"
                label="Quantity"
                placeholder="Enter quantity"
                type="number"
                required
              />

              <div></div>
            </InputContainer>

            <Title>Fee / Charges</Title>

            <InputContainer>
              <div>
                <FormInput
                  id="commodity.spread"
                  label="Spread"
                  placeholder="Enter spread"
                  type="number"
                  required
                />
              </div>

              <div>
                <FormInput
                  id="commodity.conversionFee"
                  label="Conversion fee"
                  placeholder="Enter conversion fee"
                  type="number"
                  required
                />
              </div>

              <div>
                <FormInput
                  id="commodity.overnightSwaps"
                  label="Overnight swaps"
                  placeholder="Enter overnight swaps"
                  type="number"
                  required
                />
              </div>

              <div>
                <FormInput
                  id="commodity.commission"
                  label="Commission"
                  placeholder="Enter commission"
                  type="number"
                  required
                />
              </div>

              <div>
                <FormInput
                  id="commodity.pl1"
                  label="Total P/L (display in symbol)"
                  placeholder=""
                  type="number"
                  isDisabled
                  required
                />
              </div>

              <div>
                <FormInput
                  id="commodity.pl2"
                  label="Total P/L (display in account)"
                  placeholder=""
                  type="number"
                  isDisabled
                  required
                />
              </div>

              <div>
                <FormInput
                  id="commodity.currConversionPrice"
                  label="Current conversion price"
                  placeholder="Enter current conversion price"
                  type="number"
                  required
                />
              </div>

              <div></div>

              <Button
                onClick={() => {
                  const { commodity } = getValues();

                  const pl1 =
                    (commodity.exitPrice - commodity.entryPrice) *
                      commodity.quantity -
                    (commodity.spread -
                      commodity.conversionFee -
                      commodity.overnightSwaps -
                      commodity.commission -
                      commodity.currConversionPrice);

                  const pl2 =
                    (commodity.exitPrice - commodity.entryPrice) *
                      commodity.quantity -
                    (commodity.spread -
                      commodity.conversionFee -
                      commodity.overnightSwaps -
                      commodity.commission -
                      commodity.currConversionPrice);

                  reset({
                    ...getValues(),
                    commodity: {
                      ...getValues().commodity,
                      pl1,
                      pl2,
                    },
                  });
                }}
                disabled={isCalculateDisabled}
              >
                Calculate P/L
              </Button>
            </InputContainer>

            <InputContainer>
              <FormDropdown
                id="commodity.expectation"
                label="Is the result within your expectation?"
                placeholder="Choose the expectation"
                options={expectationOptions || []}
                required
              />
            </InputContainer>

            {/* )} */}
          </SectionContainer>
        )}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              commodity: {
                ...getValues().commodity,
                ...generateRandomData(),
                pl1: null,
                pl2: null,
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Commodity;
